import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { PageHeader } from "@/shared/components/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { StatusText } from "@/shared/components/ui/status-chip";
import { TableCard, TableHeadRow, TableRow, Th, Empty } from "@/shared/components/ui/table";
import { EmptyState } from "@/shared/components/ui/empty-state";
import { useCampaignsList } from "./use-campaigns";
import type { CampaignListItem } from "./types";

const GRID = "grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr]";

/**
 * Campaigns list (screens.md §1) — the home screen, wired to `GET /api/campaigns`.
 * Rows link to the campaign Overview.
 */
export function CampaignsPage() {
  const { data, isLoading, isError, refetch, isFetching } = useCampaignsList();

  return (
    <div>
      <PageHeader
        title="Campaigns"
        actions={
          <>
            <Input placeholder="Search campaigns" className="w-[260px]" />
            <Button asChild>
              <Link to="/campaigns/new" data-testid="new-campaign">
                <Plus className="h-[14px] w-[14px]" strokeWidth={2.4} />
                New campaign
              </Link>
            </Button>
          </>
        }
        footnote="A campaign is the container everything hangs off — discovery runs, leads, audience, sequences, analytics."
      />

      {isError ? (
        <EmptyState
          message="Couldn't load campaigns. Check your connection and try again."
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      ) : !isLoading && (data?.length ?? 0) === 0 ? (
        <EmptyState
          message="No campaigns yet. Point the engine at a market to create your first one."
          action={
            <Button asChild>
              <Link to="/campaigns/new">New campaign</Link>
            </Button>
          }
        />
      ) : (
        <TableCard>
          <TableHeadRow grid={GRID}>
            <Th className="col-span-1">Name</Th>
            <Th>Status</Th>
            <Th>Leads</Th>
            <Th>Contacts</Th>
            <Th>Sequences</Th>
            <Th>Open</Th>
            <Th>Created</Th>
          </TableHeadRow>

          {isLoading ? (
            <SkeletonRows />
          ) : (
            data!.map((c) => <CampaignRow key={c.id} c={c} />)
          )}
        </TableCard>
      )}

      {isFetching && !isLoading && (
        <p className="mt-3 font-mono text-[11px] text-ge-faint">refreshing…</p>
      )}
    </div>
  );
}

function CampaignRow({ c }: { c: CampaignListItem }) {
  const navigate = useNavigate();
  const openRate = c.sent > 0 ? `${Math.round((c.opened / c.sent) * 100)}%` : null;
  return (
    <TableRow
      grid={GRID}
      data-testid={`campaign-row-${c.id}`}
      className="cursor-pointer hover:bg-ge-card"
      onClick={() => navigate(`/campaigns/${c.id}`)}
    >
      <div className="font-sans text-[13.5px] font-semibold text-ge-primary">{c.name}</div>
      <div>
        {c.status === "LAUNCHED" ? (
          <StatusText tone="accent">LAUNCHED</StatusText>
        ) : (
          <StatusText tone="faint">{c.status}</StatusText>
        )}
      </div>
      <Cell>{c.leads || <Empty />}</Cell>
      <Cell>{c.contacts || <Empty />}</Cell>
      <Cell>{c.sequences || <Empty />}</Cell>
      <Cell>
        {openRate ? (
          <span className="flex flex-col leading-tight" data-testid={`campaign-open-${c.id}`}>
            <span>{openRate}</span>
            {c.opensTotal > 0 && (
              <span className="text-[10px] text-ge-faint">{c.opensTotal} opens</span>
            )}
          </span>
        ) : (
          <Empty />
        )}
      </Cell>
      <Cell>{formatDate(c.createdAt)}</Cell>
    </TableRow>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <TableRow key={i} grid={GRID} aria-hidden data-testid="campaign-skeleton">
          <div className="h-[14px] w-2/3 animate-pulse rounded bg-ge-border-card" />
          {Array.from({ length: 6 }).map((__, j) => (
            <div key={j} className="h-[12px] w-10 animate-pulse rounded bg-ge-border-card" />
          ))}
        </TableRow>
      ))}
    </>
  );
}

function Cell({ children }: { children: React.ReactNode }) {
  return <div className="font-mono text-[12px] text-ge-value">{children}</div>;
}

function formatDate(iso: string): string {
  // API returns an ISO datetime; the table shows the date only.
  return iso.slice(0, 10);
}
